/**
 * The Tasks page's own state: the filter bar, the group-by switcher, the sort and the saved
 * personal views — and the one function that turns all of it into a `tasks.list` input.
 *
 * It is a plain module with no React in it, because that is what makes it testable: the server
 * owns grouping, paging and permission filtering (`services/tasks/queries.ts`), so the only
 * logic the browser has is *what to ask for*, and a page that computed its own groups would be
 * a second, disagreeing implementation of the rule the spec puts on the server.
 *
 * The one arithmetic here is the **two ranges**: the filter bar offers monday-ish due presets
 * (Overdue · Today · This week · Next 7 · Next 30 · Custom…) and, since P-301, a backwards
 * *Assigned on* one (Last 7 / 30 / 90 · Custom…), while `tasks.list` takes ISO
 * `dueFrom`/`dueTo` and `assignedFrom`/`assignedTo` pairs — so the translation has to happen
 * somewhere. It happens here, against an injected `now`, so a test is not hostage to the clock.
 */
import type {
  TaskDoneFilter,
  TaskFiltersInput,
  TaskGroupBy,
  TaskGroupPageInput,
  TaskGroupPageView,
  TaskGroupView,
  TaskGroupedListInput,
  TaskListByGroupResultView,
  TaskListInput,
  TaskListResultView,
  TaskRowView,
  TaskSettingsView,
  TaskSort,
  TaskTab,
} from './types';
import {
  TASK_GROUP_BY_FOR_TAB,
  type TaskAssignedRange,
  type TaskDueRange,
} from './vocabulary';

/** Everything the filter bar holds. Every field is "no filter" by default. */
export interface TaskFilterState {
  workspaceIds: string[];
  boardIds: string[];
  /** The **assignee** picker; it is offered on the assigned tab and on the archive. */
  assigneeIds: string[];
  /** The **assigner** picker; offered on My Tasks ("who assigns me the most"). */
  assignerIds: string[];
  statusLabels: string[];
  done: TaskDoneFilter;
  dueRange: TaskDueRange;
  /**
   * The two ends of `dueRange: 'custom'`, as the date inputs produce them (`YYYY-MM-DD`) —
   * P-301 §1. **Either side may be empty**: "everything before the 30th" and "everything from
   * the 1st" are both real asks, and a range that demanded both would cost the common case a
   * second gesture. They are kept while another range is selected rather than cleared, so
   * flipping to *This week* and back does not lose what was typed.
   */
  dueFrom: string;
  dueTo: string;
  /** **Assigned on** — the backwards-looking range (P-301 §1), with the same two ends. */
  assignedRange: TaskAssignedRange;
  assignedFrom: string;
  assignedTo: string;
  query: string;
  /** The "Show archived" toggle each tab carries (`docs/13` §2). */
  includeArchived: boolean;
}

export const EMPTY_TASK_FILTERS: TaskFilterState = {
  workspaceIds: [],
  boardIds: [],
  assigneeIds: [],
  assignerIds: [],
  statusLabels: [],
  done: 'any',
  dueRange: 'any',
  dueFrom: '',
  dueTo: '',
  assignedRange: 'any',
  assignedFrom: '',
  assignedTo: '',
  query: '',
  includeArchived: false,
};

/** True when nothing in the bar narrows anything — what the `Clear all` chip is enabled by. */
export function filtersAreEmpty(filters: TaskFilterState): boolean {
  return (
    filters.workspaceIds.length === 0 &&
    filters.boardIds.length === 0 &&
    filters.assigneeIds.length === 0 &&
    filters.assignerIds.length === 0 &&
    filters.statusLabels.length === 0 &&
    filters.done === 'any' &&
    !dueRangeIsSet(filters) &&
    !assignedRangeIsSet(filters) &&
    filters.query.trim() === '' &&
    filters.includeArchived === false
  );
}

/**
 * Whether the due range narrows anything.
 *
 * `custom` with both ends empty does **not**: the person opened the two fields and typed
 * nothing, which is not a filter, and counting it as one would light up the `Clear all` chip
 * over a list that had not moved. The same rule for the assigned range below, so the two cannot
 * drift.
 */
export function dueRangeIsSet(filters: TaskFilterState): boolean {
  if (filters.dueRange === 'any') return false;
  if (filters.dueRange !== 'custom') return true;
  return filters.dueFrom !== '' || filters.dueTo !== '';
}

export function assignedRangeIsSet(filters: TaskFilterState): boolean {
  if (filters.assignedRange === 'any') return false;
  if (filters.assignedRange !== 'custom') return true;
  return filters.assignedFrom !== '' || filters.assignedTo !== '';
}

/** How many chips the bar shows as "active" — the number in the `Filter (n)` button. */
export function activeFilterCount(filters: TaskFilterState): number {
  let count = 0;
  if (filters.workspaceIds.length > 0) count += 1;
  if (filters.boardIds.length > 0) count += 1;
  if (filters.assigneeIds.length > 0) count += 1;
  if (filters.assignerIds.length > 0) count += 1;
  if (filters.statusLabels.length > 0) count += 1;
  if (filters.done !== 'any') count += 1;
  if (dueRangeIsSet(filters)) count += 1;
  if (assignedRangeIsSet(filters)) count += 1;
  if (filters.query.trim() !== '') count += 1;
  if (filters.includeArchived) count += 1;
  return count;
}

/* ── the due range ────────────────────────────────────────────────────────── */

function startOfDay(now: Date): Date {
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0, 0));
}

function addDays(from: Date, days: number): Date {
  return new Date(from.getTime() + days * 86_400_000);
}

/**
 * `{ dueFrom, dueTo }` for one range, both ISO or absent.
 *
 * The week runs **Monday → Sunday**, which is what `docs/13` §2's `This week` bucket means and
 * what the server's own bucketing uses; a Sunday is therefore the *end* of the current week and
 * not the start of the next one.
 */
export function dueWindow(
  range: TaskDueRange,
  now: Date = new Date(),
  /** The two ends of `custom`, `YYYY-MM-DD`, either or both empty. */
  custom: { from?: string; to?: string } = {},
): { dueFrom?: string; dueTo?: string } {
  const today = startOfDay(now);
  switch (range) {
    case 'any':
      return {};
    case 'overdue':
      return { dueTo: today.toISOString() };
    case 'today':
      return { dueFrom: today.toISOString(), dueTo: addDays(today, 1).toISOString() };
    case 'this_week': {
      // `getUTCDay()` is 0 for Sunday; Monday-first makes Sunday the 7th day.
      const weekday = today.getUTCDay() === 0 ? 7 : today.getUTCDay();
      const monday = addDays(today, 1 - weekday);
      return { dueFrom: monday.toISOString(), dueTo: addDays(monday, 7).toISOString() };
    }
    case 'next_7':
      return { dueFrom: today.toISOString(), dueTo: addDays(today, 7).toISOString() };
    case 'next_30':
      return { dueFrom: today.toISOString(), dueTo: addDays(today, 30).toISOString() };
    case 'custom': {
      /*
       * The **end is exclusive**, like every other window here (§5.2), so the day the person
       * picked as *To* is included: they typed a date, not an instant, and "to the 30th" that
       * stopped at the 30th at 00:00 would silently drop a whole day of tasks. So `To` becomes
       * the start of the day after it, which is the one place in this file that adds a day.
       */
      const out: { dueFrom?: string; dueTo?: string } = {};
      const from = parseDayStart(custom.from);
      const to = parseDayStart(custom.to);
      if (from !== null) out.dueFrom = from.toISOString();
      if (to !== null) out.dueTo = addDays(to, 1).toISOString();
      return out;
    }
    default:
      return {};
  }
}

/**
 * `YYYY-MM-DD` → midnight **UTC** of that day, or `null` for anything else.
 *
 * UTC and not local, because a date cell is stored at midnight UTC (§5.2) and `assigned_at` is
 * compared against the same axis: a window built in the browser's zone would be one timezone
 * offset away from the rows it is meant to select, which on a UTC+X morning is a whole day.
 */
function parseDayStart(value: string | undefined): Date | null {
  if (value === undefined || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/* ── the assigned-on range (P-301 §1) ─────────────────────────────────────── */

/**
 * `{ assignedFrom, assignedTo }` for one range — the **backwards**-looking twin of
 * {@link dueWindow}.
 *
 * *Last 7 days* is `[today - 6 days, tomorrow)`: seven days **including today**, which is what
 * the words mean and what every report in this product counts. Keying it as `[today - 7, today)`
 * would answer a week that ended last night and leave today's own assignments out of "the last
 * seven days", which is the one thing somebody checking that filter would notice first.
 */
export function assignedWindow(
  range: TaskAssignedRange,
  now: Date = new Date(),
  custom: { from?: string; to?: string } = {},
): { assignedFrom?: string; assignedTo?: string } {
  const today = startOfDay(now);
  const tomorrow = addDays(today, 1);
  const lastDays = (days: number) => ({
    assignedFrom: addDays(today, -(days - 1)).toISOString(),
    assignedTo: tomorrow.toISOString(),
  });
  switch (range) {
    case 'last_7':
      return lastDays(7);
    case 'last_30':
      return lastDays(30);
    case 'last_90':
      return lastDays(90);
    case 'custom': {
      const out: { assignedFrom?: string; assignedTo?: string } = {};
      const from = parseDayStart(custom.from);
      const to = parseDayStart(custom.to);
      if (from !== null) out.assignedFrom = from.toISOString();
      if (to !== null) out.assignedTo = addDays(to, 1).toISOString();
      return out;
    }
    case 'any':
    default:
      return {};
  }
}

/* ── the request ──────────────────────────────────────────────────────────── */

export interface TaskQueryState {
  tab: TaskTab;
  groupBy: TaskGroupBy;
  sort: TaskSort;
  filters: TaskFilterState;
  limit?: number;
  cursor?: string | null;
}

/**
 * The `tasks.list` input for a page state. Empty arrays and empty strings are **omitted**, not
 * sent as `[]` / `''`: `filters.boardIds: []` would be read by the router as "these boards" and
 * narrow the answer to nothing, which is the opposite of "no board filter".
 */
export function toListInput(state: TaskQueryState, now: Date = new Date()): TaskListInput {
  const { filters } = state;
  const window = dueWindow(filters.dueRange, now, { from: filters.dueFrom, to: filters.dueTo });
  const assigned = assignedWindow(filters.assignedRange, now, {
    from: filters.assignedFrom,
    to: filters.assignedTo,
  });
  const out: TaskFiltersInput = { done: filters.done, includeArchived: filters.includeArchived };
  if (filters.workspaceIds.length > 0) out.workspaceIds = [...filters.workspaceIds];
  if (filters.boardIds.length > 0) out.boardIds = [...filters.boardIds];
  if (filters.assigneeIds.length > 0) out.assigneeIds = [...filters.assigneeIds];
  if (filters.assignerIds.length > 0) out.assignerIds = [...filters.assignerIds];
  if (filters.statusLabels.length > 0) out.statusLabels = [...filters.statusLabels];
  if (window.dueFrom) out.dueFrom = window.dueFrom;
  if (window.dueTo) out.dueTo = window.dueTo;
  if (assigned.assignedFrom) out.assignedFrom = assigned.assignedFrom;
  if (assigned.assignedTo) out.assignedTo = assigned.assignedTo;
  if (filters.query.trim() !== '') out.query = filters.query.trim();

  const input: TaskListInput = {
    tab: state.tab,
    groupBy: state.groupBy,
    sort: state.sort,
    filters: out,
    limit: state.limit ?? 50,
  };
  if (state.cursor) input.cursor = state.cursor;
  return input;
}

/** A grouping the tab does not offer falls back to the tab's first one, never to an error. */
export function normaliseGroupBy(tab: TaskTab, groupBy: TaskGroupBy): TaskGroupBy {
  const allowed = TASK_GROUP_BY_FOR_TAB[tab];
  return allowed.includes(groupBy) ? groupBy : (allowed[0] as TaskGroupBy);
}

/* ── the loaded window ────────────────────────────────────────────────────── */

/**
 * The pages loaded so far, as one list — **P-277**.
 *
 * Round T8 grew a single request's `limit` in place, which was simple and wrong: the eleventh
 * "Show more" asked for 550, `tasks.list` caps a window at 500, and the person was shown the raw
 * zod issue. The page now asks for one page of {@link TASK_PAGE_SIZE} and keeps walking the
 * opaque `cursor`, so a tab of any size loads.
 *
 * **The browser still computes no grouping.** `tasks.list` answers, on every page, the *whole*
 * tab's group table in the server's own order with the tab's own counts, and `groups[].rows` is
 * that page's share of each group (P-271). So merging pages is nothing but concatenating the
 * server's own partitions by key — there is no second grouping implementation here, which is the
 * rule `TasksPage` was built on and the reason P-271 was a one-sided fix.
 *
 * The group table and every scalar come from the **latest** page: it is the freshest answer, and
 * its counts already describe the whole tab rather than any window.
 */
export function mergeTaskPages(pages: readonly TaskListResultView[]): {
  rows: TaskRowView[];
  groups: TaskGroupView[];
  total: number;
  nextCursor: string | null;
  settings: TaskListResultView['settings'] | null;
} {
  const latest = pages.length === 0 ? null : pages[pages.length - 1]!;
  const rows: TaskRowView[] = [];
  const seen = new Set<string>();
  for (const page of pages) {
    for (const row of page.rows) {
      if (seen.has(row.id)) continue;
      seen.add(row.id);
      rows.push(row);
    }
  }

  const groups: TaskGroupView[] = (latest?.groups ?? []).map((group) => ({ ...group, rows: [] }));
  const byKey = new Map(groups.map((group) => [group.key, group]));
  const placed = new Set<string>();
  for (const page of pages) {
    for (const group of page.groups) {
      const target = byKey.get(group.key);
      // A group the newest answer no longer has (a status renamed, a board filtered away since)
      // is dropped rather than resurrected from a stale page.
      if (!target) continue;
      for (const row of group.rows) {
        if (placed.has(row.id)) continue;
        placed.add(row.id);
        target.rows.push(row);
      }
    }
  }

  return {
    rows,
    groups,
    total: latest?.total ?? 0,
    nextCursor: latest?.nextCursor ?? null,
    settings: latest?.settings ?? null,
  };
}

/* ── the loaded window, per group (P-318) ─────────────────────────────────── */

/**
 * The `tasks.listByGroup` input for a page state — {@link toListInput} without the cursor.
 *
 * The per-group read has no single position to name: each group carries its own cursor, so the
 * question ("this tab, grouped this way, sorted this way, filtered like this") and the position
 * inside one group are two different inputs. Everything else — the two date windows, the omitted
 * empty arrays — is the same translation, which is why it is the same function.
 */
export function toGroupedListInput(state: TaskQueryState, now: Date = new Date()): TaskGroupedListInput {
  return toListInput({ ...state, cursor: null }, now);
}

/** The `tasks.listGroup` input: the same question, plus **which** group and that group's cursor. */
export function toGroupPageInput(
  state: TaskQueryState,
  groupKey: string,
  cursor: string | null,
  now: Date = new Date(),
): TaskGroupPageInput {
  return { ...toGroupedListInput(state, now), groupKey, cursor };
}

/**
 * The pages loaded so far, **per group** — P-318.
 *
 * `base` is the latest `tasks.listByGroup` (every group of the tab, each with its own first page
 * and its own cursor); `extra` is what each group's own "Show more" has added, keyed by group.
 *
 * **The browser still computes no grouping**, which is the rule `TasksPage` is built on and the
 * reason P-271 was a one-sided fix: the groups, their order, their labels and their counts are
 * the server's, and this function concatenates the server's own partitions. What it adds over
 * {@link mergeTaskPages} is that the cursor is per group, so the answer carries one `cursors`
 * entry per group rather than one for the list.
 *
 * Extra pages for a group the newest answer no longer has (a status renamed, a board filtered
 * away since) are dropped rather than resurrected, exactly as the flat merge drops them.
 */
export function mergeTaskGroupPages(
  base: TaskListByGroupResultView | null,
  extra: Readonly<Record<string, readonly TaskGroupPageView[]>> = {},
): {
  rows: TaskRowView[];
  groups: TaskGroupView[];
  /** group key → that group's own cursor, `null` once the group is fully loaded. */
  cursors: Record<string, string | null>;
  total: number;
  settings: TaskSettingsView | null;
} {
  const groups: TaskGroupView[] = [];
  const cursors: Record<string, string | null> = {};
  const rows: TaskRowView[] = [];
  const seen = new Set<string>();

  for (const group of base?.groups ?? []) {
    const merged: TaskRowView[] = [];
    const take = (list: readonly TaskRowView[]) => {
      for (const row of list) {
        if (seen.has(row.id)) continue;
        seen.add(row.id);
        merged.push(row);
      }
    };
    take(group.rows);
    const pages = extra[group.key] ?? [];
    for (const page of pages) take(page.rows);
    /*
     * The group's cursor is the **newest** answer about this group: its last extra page's, or the
     * first page's when nothing has been added. A stale cursor would re-serve a page the list
     * already holds.
     */
    cursors[group.key] = pages.length > 0 ? (pages[pages.length - 1]?.nextCursor ?? null) : group.nextCursor;
    groups.push({ key: group.key, label: group.label, count: group.count, rows: merged });
    rows.push(...merged);
  }

  return { rows, groups, cursors, total: base?.total ?? 0, settings: base?.settings ?? null };
}

/** One turn of a group, and what its own "Show more" adds. A single window is capped at 500. */
export const TASK_PAGE_SIZE = 50;

/**
 * What the page says when a list cannot be loaded.
 *
 * **Never the thrown message** (P-277): a `tasks.list` input the server refuses arrives as the
 * raw zod issue array — `[{"origin":"number","code":"too_big","maximum":500,"path":["limit"]}]` —
 * and that is what the user was shown. The technical text goes to the console for whoever is
 * debugging; the page gets a sentence.
 */
export const TASK_LIST_ERROR_HINT =
  'Something went wrong while loading this list. Try again in a moment, or narrow the filters.';

export function describeListFailure(cause: unknown): string {
  if (cause instanceof Error) return cause.message;
  try {
    return JSON.stringify(cause) ?? String(cause);
  } catch {
    return String(cause);
  }
}

/* ── saved personal views ─────────────────────────────────────────────────── */

/**
 * A saved view is a named `(tab, groupBy, sort, filters)`.
 *
 * **Where they live.** `user_preferences` is a *closed* key set on the api
 * (`services/preferences.ts`), and the Tasks keys it registers are the eight of
 * `specs/M10-tasks.md` §5 — there is no `tasks.savedViews`. Adding one is a backend change and
 * this round is web-only, so they are stored per browser in `localStorage` and the gap is filed
 * as **q561**. Nothing else in the page depends on where they are kept: the store is this one
 * module, and moving it to `tasks.settings` later is a change to `readSavedViews` /
 * `writeSavedViews` and nothing else.
 */
export interface TaskSavedView {
  id: string;
  name: string;
  tab: TaskTab;
  groupBy: TaskGroupBy;
  sort: TaskSort;
  filters: TaskFilterState;
}

export const SAVED_VIEWS_STORAGE_KEY = 'elizmonday.tasks.savedViews';
export const MAX_SAVED_VIEWS = 20;

/** A stored custom range end. Anything that is not a `YYYY-MM-DD` day is "not set", never a throw. */
function coerceDay(value: unknown): string {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : '';
}

function coerceStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : [];
}

/** Anything the browser hands back that is not a saved view is dropped, never thrown on. */
export function coerceSavedViews(raw: unknown): TaskSavedView[] {
  if (!Array.isArray(raw)) return [];
  const out: TaskSavedView[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const record = entry as Record<string, unknown>;
    if (typeof record['id'] !== 'string' || typeof record['name'] !== 'string') continue;
    const filters = (record['filters'] ?? {}) as Record<string, unknown>;
    out.push({
      id: record['id'],
      name: record['name'].slice(0, 60),
      tab: (record['tab'] === 'assigned' || record['tab'] === 'archive' ? record['tab'] : 'mine') as TaskTab,
      groupBy: (typeof record['groupBy'] === 'string' ? record['groupBy'] : 'due') as TaskGroupBy,
      sort: (typeof record['sort'] === 'string' ? record['sort'] : 'due_asc') as TaskSort,
      filters: {
        ...EMPTY_TASK_FILTERS,
        workspaceIds: coerceStringArray(filters['workspaceIds']),
        boardIds: coerceStringArray(filters['boardIds']),
        assigneeIds: coerceStringArray(filters['assigneeIds']),
        assignerIds: coerceStringArray(filters['assignerIds']),
        statusLabels: coerceStringArray(filters['statusLabels']),
        done: (filters['done'] === 'open' || filters['done'] === 'done' ? filters['done'] : 'any') as TaskDoneFilter,
        dueRange: (typeof filters['dueRange'] === 'string' ? filters['dueRange'] : 'any') as TaskDueRange,
        /* P-301 · a saved view stores **both** ranges, custom ends included (`docs/13` §2). */
        dueFrom: coerceDay(filters['dueFrom']),
        dueTo: coerceDay(filters['dueTo']),
        assignedRange: (typeof filters['assignedRange'] === 'string'
          ? filters['assignedRange']
          : 'any') as TaskAssignedRange,
        assignedFrom: coerceDay(filters['assignedFrom']),
        assignedTo: coerceDay(filters['assignedTo']),
        query: typeof filters['query'] === 'string' ? filters['query'] : '',
        includeArchived: filters['includeArchived'] === true,
      },
    });
    if (out.length >= MAX_SAVED_VIEWS) break;
  }
  return out;
}

export function readSavedViews(): TaskSavedView[] {
  try {
    const raw = window.localStorage.getItem(SAVED_VIEWS_STORAGE_KEY);
    return raw === null ? [] : coerceSavedViews(JSON.parse(raw));
  } catch {
    // A private window, blocked site data, or a value another tab corrupted. No views is a
    // perfectly good answer; a thrown error in the page header is not.
    return [];
  }
}

export function writeSavedViews(views: TaskSavedView[]): void {
  try {
    window.localStorage.setItem(SAVED_VIEWS_STORAGE_KEY, JSON.stringify(views.slice(0, MAX_SAVED_VIEWS)));
  } catch {
    // Same reasoning: saving a view is a convenience, never a reason to lose the page.
  }
}

/** Appends or replaces by **name**, so saving twice under one name updates rather than piles up. */
export function upsertSavedView(views: readonly TaskSavedView[], next: TaskSavedView): TaskSavedView[] {
  const at = views.findIndex((view) => view.name.toLowerCase() === next.name.toLowerCase());
  if (at < 0) return [...views, next].slice(0, MAX_SAVED_VIEWS);
  const copy = [...views];
  copy[at] = { ...next, id: views[at]!.id };
  return copy;
}

export function removeSavedView(views: readonly TaskSavedView[], id: string): TaskSavedView[] {
  return views.filter((view) => view.id !== id);
}
